/**
 * Buyer-facing string catalog for <squiryl-button>. Dependency-free: one flat map
 * of messages per locale. The active locale is resolved at render time from the
 * `locale` attribute or the browser language, falling back to English.
 *
 * Scope note: only the shopper-visible strings are localized. Developer-facing
 * error messages (the `squiryl:error` detail), code, and the API spec stay English.
 */
export type LocaleCode = 'en' | 'es' | 'ru' | 'fr' | 'de';

export const SUPPORTED_LOCALES: readonly LocaleCode[] = ['en', 'es', 'ru', 'fr', 'de'];

export type MessageKey =
  | 'continueButton'
  | 'connecting'
  | 'scanHint'
  | 'openInApp'
  | 'waiting'
  | 'shared'
  | 'declined'
  | 'expired'
  | 'error';

export type Messages = Record<MessageKey, string>;

export const STRINGS: Record<LocaleCode, Messages> = {
  en: {
    continueButton: 'Continue with Squiryl',
    connecting: 'Connecting…',
    scanHint: 'Scan with the Squiryl app, or',
    openInApp: 'Open in the Squiryl app',
    waiting: 'Waiting for approval…',
    shared: 'Shared',
    declined: 'Declined',
    expired: 'Expired — try again',
    error: 'Something went wrong',
  },
  es: {
    continueButton: 'Continuar con Squiryl',
    connecting: 'Conectando…',
    scanHint: 'Escanea con la app de Squiryl, o',
    openInApp: 'Abrir en la app de Squiryl',
    waiting: 'Esperando aprobación…',
    shared: 'Compartido',
    declined: 'Rechazado',
    expired: 'Caducado — inténtalo de nuevo',
    error: 'Algo salió mal',
  },
  ru: {
    continueButton: 'Продолжить с Squiryl',
    connecting: 'Подключение…',
    scanHint: 'Отсканируйте приложением Squiryl или',
    openInApp: 'Открыть в приложении Squiryl',
    waiting: 'Ожидание подтверждения…',
    shared: 'Отправлено',
    declined: 'Отклонено',
    expired: 'Срок истёк — попробуйте снова',
    error: 'Что-то пошло не так',
  },
  fr: {
    continueButton: 'Continuer avec Squiryl',
    connecting: 'Connexion…',
    scanHint: "Scannez avec l'application Squiryl, ou",
    openInApp: "Ouvrir dans l'application Squiryl",
    waiting: "En attente d'approbation…",
    shared: 'Partagé',
    declined: 'Refusé',
    expired: 'Expiré — réessayez',
    error: "Une erreur s'est produite",
  },
  de: {
    continueButton: 'Mit Squiryl fortfahren',
    connecting: 'Verbindung…',
    scanHint: 'Mit der Squiryl-App scannen, oder',
    openInApp: 'In der Squiryl-App öffnen',
    waiting: 'Warten auf Bestätigung…',
    shared: 'Geteilt',
    declined: 'Abgelehnt',
    expired: 'Abgelaufen — erneut versuchen',
    error: 'Etwas ist schief gelaufen',
  },
};

function isSupported(code: string): code is LocaleCode {
  return (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

/**
 * Resolves the display locale. Uses `requested` (the `locale` attribute) when set,
 * otherwise the browser's `navigator.language`; takes the base subtag (`es-419` →
 * `es`), lowercased; falls back to `en` for anything unsupported or absent. Never
 * throws in a non-browser context.
 */
export function resolveLocale(requested?: string | null): LocaleCode {
  const raw = requested ?? readNavigatorLanguage();
  const base = (raw ?? '').trim().toLowerCase().split('-')[0] ?? '';
  return isSupported(base) ? base : 'en';
}

function readNavigatorLanguage(): string | undefined {
  const nav = (globalThis as { navigator?: { language?: string } }).navigator;
  return nav?.language;
}

/** Returns the message map for a resolved locale. */
export function messagesFor(locale: LocaleCode): Messages {
  return STRINGS[locale];
}
